import { useEffect, useRef, useState } from 'react'
import { hexToHsv, hsvToHex, normalizeLightHex } from '../lib/colorPickerUtils'
import { HUB_TOOL_IDS } from '../lib/hubTools'
import HubToolWindow from './HubToolWindow'

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const supportsEyeDropper = () =>
  typeof window !== 'undefined' && typeof window.EyeDropper === 'function'

const EyedropperIcon = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
    <path
      fill="currentColor"
      d="M11.2 1.8a1.6 1.6 0 0 1 2.3 2.3l-1.1 1.1-2.3-2.3 1.1-1.1ZM9.7 3.3 3.8 9.2l-.6 2.1 2.1-.6 5.9-5.9-2.3-2.3ZM2.6 11.8l1.6-.5-.5 1.6a.8.8 0 0 0 .2.8l1.1 1.1a.8.8 0 0 0 1.1 0l.8-.8a.8.8 0 0 0 0-1.1l-1.1-1.1a.8.8 0 0 0-.8-.2Z"
    />
  </svg>
)

const SliderRow = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (next) => String(next),
}) => (
  <label className="block space-y-1.5">
    <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-white/70">
      <span>{label}</span>
      <span className="font-mono text-white/90">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      onPointerDown={(event) => event.stopPropagation()}
      className="w-full cursor-pointer accent-fuchsia-400"
      aria-label={label}
    />
  </label>
)

const SaturationValuePicker = ({ hue, saturation, value, onChange }) => {
  const areaRef = useRef(null)

  const updateFromPointer = (clientX, clientY) => {
    const rect = areaRef.current?.getBoundingClientRect()
    if (!rect) return
    const nextSaturation = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100)
    const nextValue = clamp((1 - (clientY - rect.top) / rect.height) * 100, 0, 100)
    onChange(nextSaturation, nextValue)
  }

  const handlePointerDown = (event) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    updateFromPointer(event.clientX, event.clientY)
  }

  const handlePointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    updateFromPointer(event.clientX, event.clientY)
  }

  const handlePointerUp = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      ref={areaRef}
      className="relative h-40 w-full cursor-crosshair touch-none overflow-hidden rounded-md border border-white/20"
      style={{
        backgroundColor: `hsl(${hue} 100% 50%)`,
        backgroundImage:
          'linear-gradient(to top, rgb(0 0 0), transparent), linear-gradient(to right, rgb(255 255 255), transparent)',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      role="application"
      aria-label="Saturation and brightness"
    >
      <span
        className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
        style={{
          left: `${saturation}%`,
          top: `${100 - value}%`,
        }}
        aria-hidden="true"
      />
    </div>
  )
}

const HueSlider = ({ hue, onChange }) => {
  const trackRef = useRef(null)

  const updateFromPointer = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect) return
    const nextHue = clamp(((clientX - rect.left) / rect.width) * 360, 0, 360)
    onChange(nextHue)
  }

  const handlePointerDown = (event) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    updateFromPointer(event.clientX)
  }

  const handlePointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    updateFromPointer(event.clientX)
  }

  const handlePointerUp = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      ref={trackRef}
      className="relative h-4 w-full cursor-ew-resize touch-none rounded-full border border-white/20"
      style={{
        backgroundImage:
          'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      role="slider"
      aria-label="Hue"
      aria-valuemin={0}
      aria-valuemax={360}
      aria-valuenow={Math.round(hue)}
    >
      <span
        className="pointer-events-none absolute top-1/2 h-5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/80 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
        style={{ left: `${(hue / 360) * 100}%` }}
        aria-hidden="true"
      />
    </div>
  )
}

const ThemeColorPickerWindow = ({
  visible = false,
  fieldLabel,
  initialValue,
  matchOptions = [],
  onPreview,
  onCommit,
  onCancel,
  columnIndex = 4,
}) => {
  const [hsv, setHsv] = useState(() => hexToHsv(initialValue))
  const [eyedropperReady] = useState(supportsEyeDropper)
  const previewRef = useRef(onPreview)
  previewRef.current = onPreview
  const openSessionRef = useRef(null)

  useEffect(() => {
    if (!visible) {
      openSessionRef.current = null
      return
    }
    const sessionKey = `${fieldLabel}:${initialValue}`
    if (openSessionRef.current === sessionKey) return
    openSessionRef.current = sessionKey
    const nextHsv = hexToHsv(initialValue)
    setHsv(nextHsv)
    previewRef.current?.(hsvToHex(nextHsv.h, nextHsv.s, nextHsv.v))
  }, [visible, fieldLabel, initialValue])

  const emitDraft = (updater) => {
    setHsv((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater
      previewRef.current?.(hsvToHex(next.h, next.s, next.v))
      return next
    })
  }

  const handleHueChange = (hue) => {
    emitDraft((current) => ({ ...current, h: hue }))
  }

  const handleSaturationValueChange = (saturation, nextValue) => {
    emitDraft((current) => ({ ...current, s: saturation, v: nextValue }))
  }

  const handleSaturationChange = (saturation) => {
    emitDraft((current) => ({ ...current, s: saturation }))
  }

  const handleBrightnessChange = (nextValue) => {
    emitDraft((current) => ({ ...current, v: nextValue }))
  }

  const handleHexChange = (nextHex) => {
    setHsv((current) => {
      const normalized = normalizeLightHex(nextHex, hsvToHex(current.h, current.s, current.v))
      return hexToHsv(normalized)
    })
  }

  const handleEyedropper = async () => {
    if (!eyedropperReady) return

    try {
      const dropper = new window.EyeDropper()
      const result = await dropper.open()
      const normalized = normalizeLightHex(result.sRGBHex, hsvToHex(hsv.h, hsv.s, hsv.v))
      emitDraft(hexToHsv(normalized))
    } catch {
      // Eyedropper cancelled or failed
    }
  }

  // Copies the stored number; the eyedropper would return a display-space sample.
  const handleMatchChange = (event) => {
    const option = matchOptions.find((entry) => entry.key === event.target.value)
    if (!option) return
    emitDraft(hexToHsv(option.hex))
  }

  const handleDone = () => {
    onCommit?.(hsvToHex(hsv.h, hsv.s, hsv.v))
  }

  useEffect(() => {
    if (!visible) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      onCancel?.()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onCancel, visible])

  const currentHex = hsvToHex(hsv.h, hsv.s, hsv.v)

  return (
    <HubToolWindow
      toolId={HUB_TOOL_IDS.themeColorPicker}
      label={`${fieldLabel} color`}
      titleClassName="text-fuchsia-300"
      borderClassName="border-fuchsia-400/40"
      open={visible}
      columnIndex={columnIndex}
    >
      <div className="flex flex-col gap-3 p-3">
        <p className="text-[11px] leading-relaxed text-white/65">
          Preview updates live on the site. Done keeps the color. The red X or Escape reverts to what you had before opening this panel.
        </p>

        <SaturationValuePicker
          hue={hsv.h}
          saturation={hsv.s}
          value={hsv.v}
          onChange={handleSaturationValueChange}
        />

        <HueSlider hue={hsv.h} onChange={handleHueChange} />

        <SliderRow
          label="Saturation"
          value={hsv.s}
          min={0}
          max={100}
          step={1}
          onChange={handleSaturationChange}
          format={(next) => `${Math.round(next)}%`}
        />

        <SliderRow
          label="Brightness"
          value={hsv.v}
          min={0}
          max={100}
          step={1}
          onChange={handleBrightnessChange}
          format={(next) => `${Math.round(next)}%`}
        />

        <div className="flex items-center gap-2">
          <span
            className="h-9 w-12 shrink-0 rounded border border-white/20"
            style={{ backgroundColor: currentHex }}
            aria-hidden="true"
          />
          <input
            type="text"
            value={currentHex}
            onChange={(event) => handleHexChange(event.target.value)}
            onBlur={(event) => handleHexChange(event.target.value)}
            className="min-w-0 flex-1 rounded-md border border-white/20 bg-black/50 px-2 py-1.5 font-mono text-[11px] uppercase text-white/90"
            spellCheck={false}
            aria-label={`${fieldLabel} hex code`}
          />
          <button
            type="button"
            onClick={handleEyedropper}
            disabled={!eyedropperReady}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/20 bg-black/50 text-white/85 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Pick color from screen"
            title={
              eyedropperReady
                ? 'Eyedropper — sample a color from the page'
                : 'Eyedropper is not supported in this browser'
            }
          >
            <EyedropperIcon />
          </button>
        </div>

        {matchOptions.length > 0 ? (
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-white/70">
            <span className="shrink-0">Match</span>
            <select
              value=""
              onChange={handleMatchChange}
              onPointerDown={(event) => event.stopPropagation()}
              className="min-w-0 flex-1 rounded-md border border-white/20 bg-black/50 px-2 py-1.5 text-[11px] normal-case text-white/90"
              aria-label={`Match ${fieldLabel} to another theme color`}
            >
              <option value="">Another color…</option>
              {matchOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {`${option.groupLabel} · ${option.label} — ${option.hex}`}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <button
          type="button"
          onClick={handleDone}
          className="rounded-md border border-fuchsia-400/50 bg-fuchsia-500/20 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-fuchsia-100 hover:bg-fuchsia-500/30"
        >
          Done
        </button>
      </div>
    </HubToolWindow>
  )
}

export default ThemeColorPickerWindow
